import { useCallback, useEffect, useRef, useState } from "react"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "react-bootstrap"
import { Button } from "@inmagik/magik-ui"
import { BlockTypeSelector } from "./BlockTypeSelector"
import type { BlockBase } from "../../../types"
import { TextBlockForm } from "./blocks/TextBlockForm"
import { QuoteBlockForm } from "./blocks/QuoteBlockForm"
import { TextImageBlockForm } from "./blocks/TextImageBlockForm"
import { TextCarouselBlockForm } from "./blocks/TextCarouselBlockForm"
import { ImageBlockForm } from "./blocks/ImageBlockForm"
import { TwoImagesBlockForm } from "./blocks/TwoImagesBlockForm"
import { ImageGridBlockForm } from "./blocks/ImageGridBlockForm"
import { ImageGridTextBlockForm } from "./blocks/ImageGridTextBlockForm"
import { ImageCarouselBlockForm } from "./blocks/ImageCarouselBlockForm"

export function ModalBlock({
  block,
  setCurrentBlock,
  onClose,
  editBlock,
}: {
  block: BlockBase | null
  setCurrentBlock: (b: BlockBase | null) => void
  onClose: () => void
  editBlock: (b: BlockBase) => void
}) {
  const submitRef = useRef<HTMLButtonElement>(null)
  const [blockFormValid, setBlockFormValid] = useState(false)

  const saveBlock = useCallback(
    (b: BlockBase) => {
      setCurrentBlock(b)
      editBlock(b)
      onClose()
    },
    [editBlock, onClose, setCurrentBlock],
  )

  useEffect(() => setBlockFormValid(false), [block?.uuid, block?.blockType])

  const renderForm = () => {
    if (!block?.blockType) return null

    const commonProps = {
      block,
      saveBlock,
      setBlockFormValid,
      submitRef,
    }

    switch (block.blockType) {
      case "Text":
        return <TextBlockForm {...commonProps} />
      case "Quote":
        return <QuoteBlockForm {...commonProps} />
      case "TextImage":
        return <TextImageBlockForm {...commonProps} />
      case "TextCarousel":
        return <TextCarouselBlockForm {...commonProps} />
      case "Image":
        return <ImageBlockForm {...commonProps} />
      case "TwoImages":
        return <TwoImagesBlockForm {...commonProps} />
      case "ImageGrid":
        return <ImageGridBlockForm {...commonProps} />
      case "ImageGridText":
        return <ImageGridTextBlockForm {...commonProps} />
      case "ImageCarousel":
        return <ImageCarouselBlockForm {...commonProps} />
      default:
        return null
    }
  }

  return (
    <Modal backdrop="static" show={!!block} onHide={onClose} centered size={!!block?.blockType ? "xl" : "lg"}>
      <ModalHeader closeButton>
        <Modal.Title>{block?.uuid ? "Modifica Blocco" : "Aggiungi Blocco"}</Modal.Title>
      </ModalHeader>

      <ModalBody style={{ ...(!!block?.blockType ? { maxHeight: "70vh", overflowY: "auto" } : {}) }}>
        {!block?.blockType ? (
          <BlockTypeSelector
            onSelect={(type) => {
              if (!block) return
              setCurrentBlock({ ...block, blockType: type })
            }}
          />
        ) : (
          renderForm()
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <div id="note-block-modal" />
        <div className="d-flex gap-2">
          <Button type="button" variant="secondary" onClick={onClose} title="Chiudi" className="text-white" size="md" />
          <button ref={submitRef} disabled={!blockFormValid} type="button" className="btn btn-primary btn-md">
            Salva
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
